"use client";

import { useEffect, useState } from "react";
import { useFrame } from "react-frame-component";
import { BreakpointKey, Breakpoints } from "@template/theme";
import { useTheme } from "styled-components";

type TBreakpoint = {
  breakpoint: BreakpointKey;
  width: number;
};

const getCurrentBreakpoint = (
  window: Window,
  breakpoints: Breakpoints,
): TBreakpoint => {
  const width = window.innerWidth - 1;
  return {
    breakpoint: Object.keys(breakpoints)
      .reverse()
      .find(
        (key: BreakpointKey) => width >= breakpoints[key].value,
      ) as BreakpointKey,
    width,
  };
};
const checkBreakpoint = (
  targetBreakpoint: BreakpointKey,
  window: Window,
  breakpoints: Breakpoints,
) => {
  if (!targetBreakpoint) return false;

  const targetBreakpointWidth = breakpoints[targetBreakpoint].value;

  return window.innerWidth - 1 >= targetBreakpointWidth;
};

const useBreakpoint = (targetBreakpoint: BreakpointKey) => {
  const [mounted, setMounted] = useState(false);
  const x = useTheme();
  const { breakpoints } = useTheme();
  const [currentBreakpoint, setCurrentBreakpoint] = useState<TBreakpoint>(
    typeof window !== "undefined"
      ? getCurrentBreakpoint(window, breakpoints)
      : {
        breakpoint: "sm",
        width: 0,
      }
  );
  const [isTargetBreakpoint, setIsTargetBreakpoint] = useState<boolean>(
    typeof window !== "undefined"
      ? checkBreakpoint(targetBreakpoint, window, breakpoints)
      : true
  );
  const frame = useFrame();

  const target = frame ? frame.window : window;

  useEffect(() => {
    if (!mounted) {
      target?.addEventListener("resize", () => {
        setCurrentBreakpoint(getCurrentBreakpoint(target, breakpoints));
        if (targetBreakpoint) {
          setIsTargetBreakpoint(
            checkBreakpoint(targetBreakpoint, target, breakpoints),
          );
        }
      });
      setMounted(true);
    }
    return () => {
      target?.removeEventListener("resize", () => {
        setCurrentBreakpoint(getCurrentBreakpoint(target, breakpoints));
        if (targetBreakpoint) {
          setIsTargetBreakpoint(
            checkBreakpoint(targetBreakpoint, target, breakpoints),
          );
        }
      });
    };
  }, [mounted]);

  return {
    ...currentBreakpoint,
    check: targetBreakpoint ? isTargetBreakpoint : undefined,
    mounted,
  };
};

export default useBreakpoint;
